import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-checked", "block", "buttonRef", "children", "external", "hideExternalLinkIcon", "href", "iconProps", "onClick", "responsive", "size", "target", "truncate", "truncateStringProps", "truncateTooltip", "use", "__color"],
  _excluded2 = ["aria-pressed", "aria-invalid", "aria-current", "aria-checked", "block", "buttonRef", "children", "external", "href", "iconProps", "responsive", "size", "truncate", "truncateStringProps", "truncateTooltip", "type", "use", "__color"];
import { PrivateButton } from './PrivateButton';
import { useEffect } from 'react';
import { PrivateButtonLink } from './PrivateButtonLink';
import { getTabIndex, getTarget, warnIfShouldBeExternal } from './Button.utils';
import ShareButton from '../ShareButton/ShareButton';
import { useActive } from '../../utils/useActive';
import { useIconButtonContext } from '../IconButton/IconButtonProvider';
import { useIsInFilledToolbar } from '../ToolBar/ToolBarContext';
import { getComputedUse } from './Button.helpers';
import { useTheme, isTrellisTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
const buttonSizeShorthandMap = {
  xs: 'extra-small',
  sm: 'small',
  md: 'medium',
  'extra-small': 'extra-small',
  small: 'small',
  medium: 'medium',
  default: 'medium'
};
const ButtonComponent = props => {
  const {
    active,
    onMouseDown,
    onKeyDown,
    onKeyUp,
    onBlur
  } = useActive(props);
  const {
    autoFocus,
    buttonRef,
    disabled = false,
    tabIndex
  } = props;
  const theme = useTheme();
  const isTrellis = isTrellisTheme(theme);
  useEffect(() => {
    // Simulate autoFocus when rendering a link element
    if (autoFocus && props.href) {
      var _buttonRef$current;
      buttonRef === null || buttonRef === void 0 || (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 || _buttonRef$current.focus();
    }
  }, [autoFocus, buttonRef, props.href]);
  const computedTabIndex = getTabIndex(disabled, tabIndex);
  const iconButtonContext = useIconButtonContext();
  const isIconButton = iconButtonContext !== null && (iconButtonContext === null || iconButtonContext === void 0 ? void 0 : iconButtonContext.buttonStyleOverrides) !== undefined;
  const isInFilledToolbar = useIsInFilledToolbar();

  // If we are rendering a link instead of a button
  if (props.href !== undefined && props.href !== null) {
    const {
        'aria-checked': ariaChecked,
        block = false,
        children,
        external = false,
        hideExternalLinkIcon = false,
        href,
        iconProps,
        onClick,
        responsive = true,
        size = 'md',
        target,
        truncate = false,
        truncateStringProps,
        truncateTooltip,
        use = 'secondary',
        __color
      } = props,
      rest = _objectWithoutPropertiesLoose(props, _excluded);

    // Get the updated use based on the context

    const computedUse = getComputedUse({
      use,
      isInFilledToolbar,
      isTrellis
    });
    warnIfShouldBeExternal(external, target, 'Button', isIconButton);
    const handleLinkKeyDown = e => {
      if (typeof onKeyDown === 'function') onKeyDown(e);

      // Ignore keydown events that bubbled up
      if (e.target !== (buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current)) return;

      // If this is an <a> with a click handler, simulate click on Spacebar to be more button-like
      if (onClick) {
        if (e.key === ' ') {
          // Prevent scrolling
          e.preventDefault();
          if (disabled) {
            e.stopPropagation();
          } else {
            // Legacy UIButton behavior: Call onClick for spacebar keydown
            onClick(e);
          }
        }
      }
    };
    const handleClick = e => {
      // Block click events while disabled, since we don't actually use [disabled] in the DOM.
      // FoundationsButton does this already but Link does not
      if (disabled) {
        e.stopPropagation();
        e.preventDefault();
      } else if (typeof onClick === 'function') {
        onClick(e);
      }
    };
    return /*#__PURE__*/_jsx(PrivateButtonLink, Object.assign({
      active: active,
      "aria-checked": ariaChecked,
      block: block,
      disabled: disabled,
      external: external,
      hideExternalLinkIcon: hideExternalLinkIcon,
      href: href,
      iconProps: iconProps,
      ref: buttonRef,
      onBlur: onBlur,
      onClick: handleClick,
      onKeyDown: handleLinkKeyDown,
      onKeyUp: onKeyUp,
      onMouseDown: onMouseDown,
      responsive: responsive,
      size: buttonSizeShorthandMap[size],
      tabIndex: computedTabIndex,
      target: getTarget(external, target),
      truncate: truncate,
      truncateStringProps: truncateStringProps,
      truncateTooltip: truncateTooltip,
      use: computedUse,
      color: __color
    }, rest, {
      children: children
    }));
  }
  // We are rendering a button
  else if (props.href === undefined) {
    const {
        'aria-pressed': ariaPressed,
        'aria-invalid': ariaInvalid,
        'aria-current': ariaCurrent,
        'aria-checked': ariaChecked,
        block = false,
        children,
        external = false,
        iconProps,
        responsive = true,
        size = 'md',
        truncate = false,
        truncateStringProps,
        truncateTooltip,
        type = 'button',
        use = 'secondary',
        __color
      } = props,
      rest = _objectWithoutPropertiesLoose(props, _excluded2);
    const computedUse = getComputedUse({
      use,
      isInFilledToolbar,
      isTrellis
    });
    return /*#__PURE__*/_jsx(PrivateButton, Object.assign({
      active: active,
      block: block,
      disabled: disabled,
      external: external,
      iconProps: iconProps,
      onBlur: onBlur,
      onKeyDown: onKeyDown,
      onKeyUp: onKeyUp,
      onMouseDown: onMouseDown,
      pressed: ariaPressed != null ? ariaPressed : active || undefined,
      "aria-invalid": ariaInvalid,
      "aria-current": ariaCurrent,
      "aria-checked": ariaChecked,
      ref: buttonRef,
      responsive: responsive,
      size: buttonSizeShorthandMap[size],
      tabIndex: computedTabIndex,
      truncate: truncate,
      truncateStringProps: truncateStringProps,
      truncateTooltip: truncateTooltip,
      type: type,
      use: computedUse,
      color: __color
    }, rest, {
      children: children
    }));
  }
};
export const Button = ShareButton(ButtonComponent);